import { useState } from "react";
import { 
  Palette, FileText, CreditCard, Briefcase, 
  CheckCircle2, ArrowRight, Award, Sparkles,
  Clock, Shield, MessageSquare, Send, Star, Eye
} from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import SEO from "@/components/SEO";

const WirGestaltenPage = () => {
  const { toast } = useToast();
  const [portfolioFilter, setPortfolioFilter] = useState<string>("all");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    product: "",
    budget: "",
    deadline: "",
    message: ""
  });

  const services = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: "Speisekarten",
      outcome: "Mehr Bestellungen durch klare Struktur & appetitliche Präsentation",
      bullets: [
        "Professionelle Typografie & Layout",
        "Appetitliche Food-Fotografie",
        "Druckfertige Daten in allen Formaten"
      ],
      color: "from-orange-500 to-red-500",
      isLarge: true,
      thumbnail: "speisekarte"
    },
    {
      icon: <Palette className="h-8 w-8" />,
      title: "Corporate Design",
      outcome: "Einheitliche Markenidentität für alle deine Druckprodukte",
      bullets: [
        "Logo-Entwicklung & Branding",
        "Farbpalette & Typografie",
        "Styleguide für Konsistenz"
      ],
      color: "from-primary to-primary-light",
      isLarge: true,
      thumbnail: "corporate"
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      title: "Gutscheine",
      outcome: "Hochwertige Geschenkgutscheine, die zum Einlösen einladen",
      bullets: [
        "Premium-Materialien",
        "Individuelles Design",
        "Beschreib-/bestempelbar"
      ],
      color: "from-primary to-primary-light",
      isLarge: false,
      thumbnail: "gutschein"
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Flyer",
      outcome: "Auffällige Flyer für deine Marketing-Kampagnen",
      bullets: [
        "Eye-catching Design",
        "Klare Call-to-Actions",
        "Druckoptimiert"
      ],
      color: "from-purple-500 to-pink-500",
      isLarge: false,
      thumbnail: "flyer"
    },
    {
      icon: <Briefcase className="h-6 w-6" />,
      title: "Visitenkarten",
      outcome: "Professionelle Visitenkarten mit deinem Corporate Design",
      bullets: [
        "Premium-Papiere",
        "Matt oder glänzend",
        "Individuelle Formate"
      ],
      color: "from-yellow-500 to-orange-500",
      isLarge: false,
      thumbnail: "visitenkarte"
    }
  ];

  const portfolioItems = [
    { id: "1", category: "speisekarten", title: "Italienische Speisekarte", type: "Wire-O Bindung", description: "Moderne Speisekarte mit klarer Struktur und appetitlicher Präsentation der Gerichte." },
    { id: "2", category: "speisekarten", title: "Asiatische Speisekarte", type: "Faltkarte", description: "Elegante Faltkarte mit asiatischen Design-Elementen und übersichtlicher Menüstruktur." },
    { id: "3", category: "flyer", title: "Sommer-Special Flyer", type: "DIN A5", description: "Auffälliger Flyer für saisonale Angebote mit klaren Call-to-Actions." },
    { id: "4", category: "gutscheine", title: "Geschenkgutschein Premium", type: "DINLang", description: "Hochwertiger Geschenkgutschein mit Premium-Design und individueller Gestaltung." },
    { id: "5", category: "visitenkarten", title: "Restaurant Visitenkarte", type: "Standard", description: "Professionelle Visitenkarte mit Corporate Design und hochwertigem Material." },
    { id: "6", category: "corporate", title: "Corporate Design Set", type: "Komplett", description: "Vollständiges Corporate Design inklusive Logo, Farbpalette und Styleguide." },
    { id: "7", category: "speisekarten", title: "Deutsche Küche", type: "Wire-O", description: "Traditionelle Speisekarte mit modernem Layout und regionalen Highlights." },
    { id: "8", category: "flyer", title: "Event-Flyer", type: "DIN A4", description: "Großformatiger Event-Flyer mit dynamischem Design und Event-Informationen." },
    { id: "9", category: "gutscheine", title: "Gutschein Classic", type: "DINLang", description: "Klassischer Geschenkgutschein mit zeitlosem Design und Premium-Qualität." }
  ];

  const packages = [
    {
      name: "Starter",
      price: "ab 199€",
      delivery: "24–48h",
      revisions: "1 Korrekturschleife",
      description: "Perfekt für einzelne Druckprodukte",
      features: [
        "1 Design (Gutschein, Flyer oder Visitenkarte)",
        "Druckfertige Daten",
        "1 Revision",
        "E-Mail Support"
      ],
      cta: "Starter wählen",
      popular: false
    },
    {
      name: "Pro",
      price: "ab 499€",
      delivery: "48–72h",
      revisions: "2 Korrekturschleifen",
      description: "Ideal für Speisekarten & kleine Sets",
      features: [
        "Speisekarte + 1 Druckprodukt",
        "Druckfertige Daten",
        "2 Revisionen",
        "Telefon & E-Mail Support",
        "Materialberatung"
      ],
      cta: "Pro wählen",
      popular: true
    },
    {
      name: "Premium",
      price: "ab 999€",
      delivery: "72–120h",
      revisions: "Unbegrenzte Revisionen",
      description: "Komplettes Corporate Design",
      features: [
        "Corporate Design (Logo, Farben, Typo)",
        "Speisekarte + alle Druckprodukte",
        "Unbegrenzte Revisionen",
        "Priority Support",
        "Materialberatung & Druckkoordination",
        "Styleguide"
      ],
      cta: "Premium wählen",
      popular: false
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Anfrage & Briefing",
      description: "Sag uns, was du brauchst. Wir klären alle Details in einem kurzen Gespräch.",
      icon: <MessageSquare className="h-6 w-6" />
    },
    {
      step: "2",
      title: "1. Entwurf",
      description: "Wir erstellen den ersten Entwurf basierend auf deinen Wünschen und deiner Marke.",
      icon: <Palette className="h-6 w-6" />
    },
    {
      step: "3",
      title: "Feedback & Anpassungen",
      description: "Du gibst Feedback, wir optimieren – bis alles perfekt ist.",
      icon: <Sparkles className="h-6 w-6" />
    },
    {
      step: "4",
      title: "Druckdaten & Lieferung",
      description: "Du erhältst druckfertige Daten in allen benötigten Formaten.",
      icon: <Send className="h-6 w-6" />
    }
  ];

  const qualityPoints = [
    {
      title: "Druckfertige Qualität",
      description: "Alle Daten sind druckfertig und in den gängigen Formaten verfügbar. Keine Nacharbeit nötig."
    },
    {
      title: "Markenkonform",
      description: "Wir achten auf Konsistenz mit deiner bestehenden Marke oder entwickeln eine neue, die zu dir passt."
    },
    {
      title: "Schnelle Umsetzung",
      description: "Typisch 24–72h für erste Entwürfe. Wir halten Termine ein und kommunizieren transparent."
    }
  ];

  const testimonials = [
    {
      quote: "Die neue Speisekarte hat unsere Bestellwerte deutlich erhöht. Professionell und appetitlich!",
      author: "Maria S.",
      restaurant: "Ristorante Bella",
      location: "Berlin"
    },
    {
      quote: "Endlich ein einheitliches Design für alle unsere Druckprodukte. Sehr zufrieden!",
      author: "Thomas K.",
      restaurant: "Bistro am Markt",
      location: "München"
    }
  ];

  const portfolioCategories = [
    { id: "all", label: "Alle" },
    { id: "speisekarten", label: "Speisekarten" },
    { id: "flyer", label: "Flyer" },
    { id: "gutscheine", label: "Gutscheine" },
    { id: "visitenkarten", label: "Visitenkarten" },
    { id: "corporate", label: "Corporate" }
  ];

  const filteredPortfolio = portfolioFilter === "all" 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === portfolioFilter);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Anfrage gesendet!",
      description: "Wir melden uns innerhalb von 24 Stunden bei dir.",
    });
    setFormData({ name: "", email: "", phone: "", product: "", budget: "", deadline: "", message: "" });
  };

  // Mockup Placeholder Component
  const MockupPlaceholder = ({ type, className = "" }: { type: string; className?: string }) => {
    const mockups = {
      speisekarte: (
        <div className={`w-full h-64 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border-2 border-orange-200 flex items-center justify-center shadow-lg ${className}`}>
          <FileText className="h-16 w-16 text-orange-500" />
        </div>
      ),
      gutschein: (
        <div className={`w-full h-48 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl border-2 border-primary/30 flex items-center justify-center shadow-lg ${className}`}>
          <CreditCard className="h-12 w-12 text-primary" />
        </div>
      ),
      visitenkarte: (
        <div className={`w-full h-32 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200 flex items-center justify-center shadow-lg ${className}`}>
          <Briefcase className="h-10 w-10 text-purple-500" />
        </div>
      ),
      corporate: (
        <div className={`w-full h-48 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border-2 border-primary/20 flex items-center justify-center shadow-lg ${className}`}>
          <Palette className="h-12 w-12 text-primary" />
        </div>
      ),
      flyer: (
        <div className={`w-full h-48 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 flex items-center justify-center shadow-lg ${className}`}>
          <FileText className="h-12 w-12 text-purple-500" />
        </div>
      )
    };
    return mockups[type as keyof typeof mockups] || <div className={`w-full h-48 bg-gray-100 rounded-xl ${className}`}></div>;
  };

  // Portfolio Preview Component (höherwertige Mockups)
  const PortfolioPreview = ({ item }: { item: typeof portfolioItems[0] }) => {
    const categoryThumbnails: Record<string, string> = {
      speisekarten: "speisekarte",
      gutscheine: "gutschein",
      visitenkarten: "visitenkarte",
      corporate: "corporate",
      flyer: "flyer"
    };
    return (
      <div className="aspect-[4/3] bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl overflow-hidden relative group">
        <MockupPlaceholder type={categoryThumbnails[item.category] || "speisekarte"} className="w-full h-full rounded-xl" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
          <div className="text-white">
            <p className="font-semibold text-sm mb-1">{item.title}</p>
            <p className="text-xs text-white/80">{item.type}</p>
          </div>
        </div>
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Eye className="h-4 w-4 text-primary" aria-hidden="true" />
        </div>
      </div>
    );
  };

  const stats = {
    designsCreated: "500+",
    typicalDelivery: "24–72h",
    printReady: "100%",
    lastUpdated: "Januar 2026"
  };

  return (
    <main className="min-h-screen bg-white">
      <SEO 
        title="Speisekarten & Printdesign für Restaurants"
        description="Professionelles Design für Speisekarten, Flyer, Gutscheine und Corporate Design – druckfertig, markenkonform, schnell."
      />
      {/* Hero Section - Premium */}
      <section className="relative bg-gradient-to-br from-primary-dark via-primary to-primary-light text-white overflow-hidden">
        {/* Grain Texture Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}></div>
        
        {/* Soft Glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-primary-light/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl opacity-60"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="animate-fade-in-up">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 tracking-tight leading-[1.15] pb-2">
                Design, das verkauft.
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-xl">
                Speisekarten, Flyer, Gutscheine & Corporate Design – druckfertig, markenkonform, schnell.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link href="#anfrage">
                  <Button
                    size="lg"
                    className="bg-white text-primary hover:bg-white/95 hover:shadow-2xl rounded-full px-8 py-6 text-lg font-semibold focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300"
                    aria-label="Angebot anfragen"
                  >
                    Angebot anfragen
                    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Button>
                </Link>
                <Link href="#portfolio">
                  <Button
                    variant="ghost"
                    size="lg"
                    className="text-white hover:bg-white/10 hover:text-white rounded-full px-6 py-6 text-base font-medium focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300 border border-white/20"
                    aria-label="Portfolio ansehen"
                  >
                    Portfolio ansehen
                  </Button>
                </Link>
              </div>

              {/* Mini-Proof Chips */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 h-8">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  500+ Designs
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 h-8">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  24–72h
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 h-8">
                  <Shield className="h-3.5 w-3.5" aria-hidden="true" />
                  Druckfertig
                </span>
              </div>
            </div>

            {/* Right: Mockup Showcase */}
            <div className="relative hidden lg:block animate-fade-in-up animation-delay-200">
              <div className="relative">
                {/* Overlapping Mockups */}
                <div className="absolute top-0 right-0 w-64 h-80 transform rotate-3 hover:rotate-6 transition-transform duration-300 hover:-translate-y-2">
                  <MockupPlaceholder type="speisekarte" className="shadow-2xl" />
                </div>
                <div className="absolute top-8 right-32 w-56 h-72 transform -rotate-2 hover:-rotate-4 transition-transform duration-300 hover:-translate-y-2">
                  <MockupPlaceholder type="gutschein" className="shadow-2xl" />
                </div>
                <div className="absolute top-16 right-64 w-48 h-64 transform rotate-1 hover:rotate-3 transition-transform duration-300 hover:-translate-y-2">
                  <MockupPlaceholder type="visitenkarte" className="shadow-2xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Editorial */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 overflow-visible">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Unsere Leistungen
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Professionelles Design für alle deine Druckprodukte
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => (
            <article
              key={index}
              className={`bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-primary hover:shadow-xl hover:shadow-primary/10 transition-colors duration-300 flex flex-col animate-fade-in-up overflow-visible ${
                service.isLarge ? "md:col-span-2 lg:col-span-1" : ""
              } ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
            >
              {/* Mini-Thumbnail */}
              <div className="mb-6">
                <MockupPlaceholder type={service.thumbnail} className="w-full h-32 rounded-xl" />
              </div>

              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white mb-6 shadow-lg`} aria-hidden="true">
                {service.icon}
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {service.title}
              </h3>
              
              {/* Outcome-Satz größer */}
              <p className="text-lg text-gray-900 leading-relaxed mb-6 font-semibold">
                {service.outcome}
              </p>
              
              <ul className="space-y-3 flex-grow">
                {service.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex items-start text-gray-600 text-base leading-relaxed">
                    <CheckCircle2 className="h-4 w-4 text-primary mr-2.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="bg-gradient-to-br from-gray-50 to-white py-24 overflow-visible">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Portfolio
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Beispiele unserer Arbeit
            </p>
          </div>

          {/* Filter Tabs - visuell stärker */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {portfolioCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setPortfolioFilter(cat.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  portfolioFilter === cat.id
                    ? "bg-primary text-white shadow-lg scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200 hover:border-primary/30"
                }`}
                aria-label={`Filter: ${cat.label}`}
                aria-pressed={portfolioFilter === cat.id}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
            {filteredPortfolio.map((item) => (
              <Dialog key={item.id}>
                <DialogTrigger asChild>
                  <div
                    className="group cursor-pointer bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-primary hover:shadow-xl transition-colors duration-300 animate-fade-in-up overflow-visible"
                  >
                    <PortfolioPreview item={item} />
                    <div className="p-5">
                      <h3 className="font-semibold text-gray-900 mb-1 text-lg">{item.title}</h3>
                      <p className="text-sm text-gray-500">{item.type}</p>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">{item.title}</DialogTitle>
                    <DialogDescription className="text-base">
                      {item.type} • {item.description}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <MockupPlaceholder type={item.category === "speisekarten" ? "speisekarte" : item.category === "gutscheine" ? "gutschein" : item.category === "visitenkarten" ? "visitenkarte" : item.category === "corporate" ? "corporate" : "flyer"} className="w-full h-auto min-h-[400px]" />
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <Link href="#anfrage">
                      <Button className="w-full bg-primary text-white hover:bg-primary-dark rounded-full px-8 py-6 text-lg font-semibold">
                        So etwas will ich auch
                        <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                      </Button>
                    </Link>
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
      </section>

      {/* Packages & Pricing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 overflow-visible">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Pakete & Preise
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Wähle das Paket, das zu dir passt
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 overflow-visible">
          {packages.map((pkg, index) => (
            <article
              key={index}
              className={`relative overflow-visible pt-4 ${
                index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`
              }`}
            >
              {/* Wrapper für Badge (overflow-visible) */}
              <div className={`relative bg-white rounded-3xl p-8 border-2 transition-colors duration-300 animate-fade-in-up ${
                pkg.popular
                  ? "border-primary shadow-xl shadow-primary/10 scale-105"
                  : "border-gray-100 hover:border-primary/50 hover:shadow-lg"
              }`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-primary text-white px-5 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                      Empfohlen
                    </span>
                  </div>
                )}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-primary">{pkg.price}</span>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Lieferzeit: <strong>{pkg.delivery}</strong>
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {pkg.revisions}
                </p>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed text-base">{pkg.description}</p>
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-gray-700 leading-relaxed text-base">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link href="#anfrage">
                <Button
                  className={`w-full rounded-full ${
                    pkg.popular
                      ? "bg-primary text-white hover:bg-primary-dark"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  }`}
                  size="lg"
                >
                  {pkg.cta}
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
              {/* Microcopy unter CTA */}
              <p className="text-xs text-gray-500 text-center mt-3 leading-relaxed">
                Unverbindlich • Antwort innerhalb von 24h
              </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Process Timeline */}
      <section className="bg-gradient-to-br from-gray-50 to-white pt-24 pb-32 overflow-visible">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              So läuft's ab
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-2">
              Keine Sorge – wir führen dich durch.
            </p>
            <p className="text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
              Wir führen dich durch Briefing → Entwurf → Feedback → Druckdaten.
            </p>
          </div>

          {/* Timeline - Desktop Horizontal, Mobile Vertical */}
          <div className="relative overflow-visible">
            {/* Desktop: Horizontal Timeline */}
            <div className="hidden md:flex items-start justify-between relative overflow-visible">
              <div className="absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-primary-light to-primary"></div>
              {processSteps.map((step, index) => (
                <div key={index} className="flex-1 relative animate-fade-in-up animation-delay-200 overflow-visible">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-28 h-28 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white shadow-lg mb-10 relative z-10">
                      {step.icon}
                    </div>
                    <div className="bg-white rounded-2xl p-8 border-2 border-gray-100 shadow-sm ring-1 ring-black/5 w-full max-w-xs min-h-[200px] pb-10">
                      <div className="text-2xl font-bold text-primary mb-3">Schritt {step.step}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed text-base">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: Vertical Timeline */}
            <div className="md:hidden space-y-10 overflow-visible pb-6">
              {processSteps.map((step, index) => (
                <div key={index} className="flex gap-4 animate-fade-in-up overflow-visible">
                  <div className="flex-shrink-0">
                    <div className="w-18 h-18 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white shadow-lg">
                      {step.icon}
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className="w-0.5 h-full bg-gradient-to-b from-primary to-primary-light ml-9 -mt-2" style={{ height: 'calc(100% + 2rem)' }}></div>
                    )}
                  </div>
                  <div className="flex-1 bg-white rounded-2xl p-6 border-2 border-gray-100 shadow-sm ring-1 ring-black/5 min-h-[160px] pb-8">
                    <div className="text-xl font-bold text-primary mb-2">Schritt {step.step}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Upgraded */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 overflow-visible">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Warum uns wählen?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Qualität, die überzeugt
          </p>
        </div>

        {/* Quality Points */}
        <div className="grid md:grid-cols-3 gap-8 mb-16 overflow-visible">
          {qualityPoints.map((point, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-primary hover:shadow-lg transition-colors duration-300 animate-fade-in-up overflow-visible"
            >
              <Award className="h-10 w-10 text-primary mb-4" aria-hidden="true" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
              <p className="text-gray-600 leading-relaxed text-base">{point.description}</p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-2 gap-8 mb-16 overflow-visible">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 border-2 border-primary/20 animate-fade-in-up overflow-visible"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" aria-hidden="true" />
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-4 text-lg italic">
                "{testimonial.quote}"
              </p>
              <div>
                <p className="font-semibold text-gray-900">{testimonial.author}</p>
                <p className="text-sm text-gray-600">{testimonial.restaurant}, {testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 text-center overflow-visible">
          <div>
            <div className="text-4xl font-bold text-primary mb-2">{stats.designsCreated}</div>
            <div className="text-gray-600 text-base">Designs erstellt</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-primary mb-2">{stats.typicalDelivery}</div>
            <div className="text-gray-600 text-base">Typische Lieferzeit</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-primary mb-2">{stats.printReady}</div>
            <div className="text-gray-600 text-base">Druckfertig</div>
          </div>
        </div>
        <p className="text-center text-xs text-gray-500 mt-4">
          Stand: {stats.lastUpdated}
        </p>
      </section>

      {/* Final CTA with Form */}
      <section id="anfrage" className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-24 overflow-visible">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Sag uns, was du brauchst
            </h2>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
              Wir antworten innerhalb von 24 Stunden mit einem unverbindlichen Angebot.
            </p>
          </div>

          <form onSubmit={handleFormSubmit} className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20 overflow-visible">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2 text-white">
                  Name *
                </label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 h-12 text-base"
                  placeholder="Dein Name"
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2 text-white">
                  E-Mail *
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 h-12 text-base"
                  placeholder="deine@email.de"
                  aria-required="true"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2 text-white">
                  Telefon (optional)
                </label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 h-12 text-base"
                  placeholder="+49 123 456789"
                />
              </div>
              <div>
                <label htmlFor="product" className="block text-sm font-medium mb-2 text-white">
                  Wunschprodukt
                </label>
                <Input
                  id="product"
                  type="text"
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 h-12 text-base"
                  placeholder="z.B. Speisekarte, Gutschein..."
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="budget" className="block text-sm font-medium mb-2 text-white">
                  Budget (optional)
                </label>
                <Input
                  id="budget"
                  type="text"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 h-12 text-base"
                  placeholder="z.B. 500€"
                />
              </div>
              <div>
                <label htmlFor="deadline" className="block text-sm font-medium mb-2 text-white">
                  Deadline (optional)
                </label>
                <Input
                  id="deadline"
                  type="text"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 h-12 text-base"
                  placeholder="z.B. in 2 Wochen"
                />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-medium mb-2 text-white">
                Nachricht
              </label>
              <Textarea
                id="message"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/50 focus:ring-2 focus:ring-white/50 text-base"
                placeholder="Erzähl uns von deinem Projekt..."
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Button
                type="submit"
                size="lg"
                className="bg-white text-primary hover:bg-white/95 rounded-full px-8 py-6 text-lg font-semibold flex-1 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
              >
                Angebot anfragen
                <Send className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
              <Link href="/warum-speiseguru">
                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  className="text-white hover:bg-white/10 rounded-full px-6 py-6 border border-white/20 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                >
                  Mehr über SpeiseGURU
                </Button>
              </Link>
            </div>
            {/* Trust-Zeile */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-white/80 pt-4 border-t border-white/10">
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" aria-hidden="true" />
                Keine Verpflichtung
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                Schnelle Antwort
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Druckfertig
              </span>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

export default WirGestaltenPage;
